import React from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../../../constants';
import { ROUTES } from '../../../routes';
import FadeImage from '../../shared/FadeImage';

/**
 * SkillCardsList — Lista compacta de cards originales que contienen la skill.
 * Usa el mismo patrón de URL que CharacterCardsDisplay.js.
 * Props: cards = array de { card_id, Category, star, title_en, reference, char_name_en, char_id }
 */

const sanitize = (str) => {
    if (typeof str !== 'string') return '';
    return str.replace(/[\\/*?":<>|]/g, '').replace(/ /g, '_');
};

const getCardImageUrl = (card) => {
    try {
        const charName = sanitize(card.char_name_en);
        const reference = sanitize(card.reference);
        const title = sanitize(card.title_en);
        const path = `/images/character/${charName}_${reference}_${title}.png`;
        return `${API_BASE_URL}/api/thumb?img=${path}&width=128`;
    } catch {
        return '';
    }
};

const CATEGORY_COLORS = {
    'Unique': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    'Innate': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    'Awakening': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
};

const CardItem = ({ card }) => {
    const imgUrl = getCardImageUrl(card);
    const categoryClass = CATEGORY_COLORS[card.Category] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    // Misma lógica que OriginalDisplay.js: slug = name_en lowercase con guiones
    const slug = card.char_name_en?.toLowerCase().replace(/ /g, '-') || '';

    return (
        <Link
            to={`${ROUTES.CHARACTER}/${slug}`}
            className="flex flex-col items-center gap-1.5 group w-[72px]"
            title={`${card.char_name_en} — ${card.title_en}`}
        >
            {/* Imagen circular pequeña */}
            <div className="w-14 h-14 rounded-full overflow-hidden border-2
                border-gray-200 dark:border-gray-600
                group-hover:border-blue-400 dark:group-hover:border-blue-500
                transition-colors shadow-sm flex-shrink-0">
                <FadeImage
                    src={imgUrl}
                    alt={card.title_en}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                />
            </div>

            {/* Nombre del personaje */}
            <p className="text-[10px] font-semibold text-center text-gray-700 dark:text-gray-300
                group-hover:text-blue-600 dark:group-hover:text-blue-400
                transition-colors leading-tight line-clamp-2 w-full">
                {card.char_name_en}
            </p>

            {/* Badge de categoría */}
            {card.Category && (
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${categoryClass}`}>
                    {card.Category}
                </span>
            )}
        </Link>
    );
};

const SkillCardsList = ({ cards }) => {
    if (!cards || cards.length === 0) return null;

    return (
        <div className="space-y-3">
            {/* Header de sección */}
            <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 px-2">
                    Available on Cards ({cards.length})
                </span>
                <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
            </div>

            {/* Grid wrap — se expande verticalmente sin scroll horizontal */}
            <div className="flex flex-wrap gap-4">
                {cards.map((card) => (
                    <CardItem key={`${card.card_id}-${card.Category}`} card={card} />
                ))}
            </div>
        </div>
    );
};

export default SkillCardsList;
